import React, { memo, useCallback } from 'react';
import {
  View,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Alert,
  StyleSheet,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import { launchImageLibrary } from 'react-native-image-picker';
import { CORK_BOARD_FILE_UPLOAD } from '@/constants/residents/corkBoardConstants';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import type { LocalAttachment, CorkBoardAttachment } from '@/types/residents/corkBoard';

interface AttachmentPickerProps {
  attachments: LocalAttachment[];
  existingAttachments?: CorkBoardAttachment[];
  onAddFiles: (files: LocalAttachment[]) => void;
  onRemoveFile: (index: number) => void;
  onRemoveExistingAttachment?: (id: string) => void;
  maxFiles?: number;
  isSubmitting?: boolean;
  uploadProgress?: number | null;
  readonly?: boolean;
  error?: string;
}

const generateId = () => `${Date.now()}_${Math.floor(Math.random() * 10000)}`;

const EXTENSION_TO_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

function inferMimeType(assetType?: string | null, fileName?: string | null): string {
  const allowed = CORK_BOARD_FILE_UPLOAD.allowedTypes as readonly string[];
  if (assetType && allowed.includes(assetType)) return assetType;
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (ext && EXTENSION_TO_MIME[ext]) return EXTENSION_TO_MIME[ext];
  return assetType || 'application/octet-stream';
}

function isImageType(mimeType: string | null | undefined): boolean {
  return !!mimeType && mimeType.startsWith('image/');
}

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function validateFile(file: { type: string; size: number; name: string }): string | null {
  const allowed = CORK_BOARD_FILE_UPLOAD.allowedTypes as readonly string[];
  if (!allowed.includes(file.type)) {
    return `${file.name}: File type not supported`;
  }
  if (file.size > CORK_BOARD_FILE_UPLOAD.maxSizeBytes) {
    return `${file.name}: Exceeds ${CORK_BOARD_FILE_UPLOAD.maxSizeMB}MB limit`;
  }
  return null;
}

export const AttachmentPicker: React.FC<AttachmentPickerProps> = memo(({
  attachments,
  existingAttachments = [],
  onAddFiles,
  onRemoveFile,
  onRemoveExistingAttachment,
  maxFiles = CORK_BOARD_FILE_UPLOAD.maxFiles,
  isSubmitting = false,
  uploadProgress,
  readonly = false,
  error,
}) => {
  const styles = useThemedStyles(makeStyles);
  const totalFiles = attachments.length + existingAttachments.length;
  const remainingSlots = Math.max(maxFiles - totalFiles, 0);
  const canAddMore = remainingSlots > 0 && !isSubmitting && !readonly;
  const showProgress = isSubmitting && uploadProgress != null;

  const handlePickFiles = useCallback(() => {
    if (!canAddMore) return;

    if (Platform.OS === 'web') {
      const input = (globalThis as any).document.createElement('input');
      input.type = 'file';
      input.accept = CORK_BOARD_FILE_UPLOAD.allowedTypes.join(',');
      input.multiple = remainingSlots > 1;

      input.onchange = () => {
        const selected = Array.from(input.files || []) as File[];
        const valid: LocalAttachment[] = [];

        for (const file of selected.slice(0, remainingSlots)) {
          const err = validateFile({ type: file.type, size: file.size, name: file.name });
          if (!err) {
            valid.push({
              uri: URL.createObjectURL(file),
              name: file.name,
              type: file.type,
              size: file.size,
              file,
            });
          }
        }
        if (valid.length > 0) onAddFiles(valid);
      };
      input.click();
      return;
    }

    launchImageLibrary(
      { mediaType: 'mixed', selectionLimit: remainingSlots, quality: 0.8 },
      (response) => {
        if (response.didCancel) return;
        if (response.errorCode) {
          const msg = response.errorCode === 'permission'
            ? 'Photo library access was denied. Please enable it in your device settings.'
            : response.errorMessage || 'Could not open photo library. Please try again.';
          Alert.alert('Unable to Access Photos', msg);
          return;
        }

        const assets = response.assets ?? [];
        const valid: LocalAttachment[] = [];
        const rejected: string[] = [];

        for (const asset of assets) {
          if (!asset.uri) continue;
          const fileName = asset.fileName || `attachment_${generateId()}`;
          const fileInfo = {
            type: inferMimeType(asset.type, asset.fileName),
            size: asset.fileSize || 0,
            name: fileName,
          };
          const err = validateFile(fileInfo);
          if (!err) {
            valid.push({ uri: asset.uri, ...fileInfo });
          } else {
            rejected.push(err);
          }
        }

        if (valid.length > 0) onAddFiles(valid);

        if (rejected.length > 0 && valid.length === 0) {
          Alert.alert('Files Not Supported', rejected.join('\n'));
        }
      },
    );
  }, [canAddMore, remainingSlots, onAddFiles]);

  return (
    <View style={styles.container}>
      <View style={styles.labelRow}>
        <Text style={styles.label}>Attachments</Text>
        <Text style={styles.counter}>{totalFiles}/{maxFiles}</Text>
      </View>

      {(existingAttachments.length > 0 || attachments.length > 0) && (
        <View style={styles.previewGrid}>
          {existingAttachments.map((att) => (
            <View key={att.id} style={styles.previewItem}>
              {isImageType(att.fileType) ? (
                <Image source={{ uri: att.fileUrl }} style={styles.previewImage} resizeMode="cover" />
              ) : (
                <View style={styles.filePreview}>
                  <Text style={styles.fileName} numberOfLines={1}>{att.fileName}</Text>
                </View>
              )}
              {!isSubmitting && !readonly && onRemoveExistingAttachment && (
                <TouchableOpacity
                  style={styles.removeButton}
                  onPress={() => onRemoveExistingAttachment(att.id)}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Text style={styles.removeIcon}>✕</Text>
                </TouchableOpacity>
              )}
            </View>
          ))}

          {attachments.map((att, index) => (
            <View key={`local-${index}`} style={styles.previewItem}>
              {isImageType(att.type) ? (
                <Image source={{ uri: att.uri }} style={styles.previewImage} resizeMode="cover" />
              ) : (
                <View style={styles.filePreview}>
                  <Text style={styles.fileName} numberOfLines={1}>{att.name}</Text>
                  {att.size ? (
                    <Text style={styles.fileSize}>{formatFileSize(att.size)}</Text>
                  ) : null}
                </View>
              )}
              {!isSubmitting && !readonly && (
                <TouchableOpacity
                  style={styles.removeButton}
                  onPress={() => onRemoveFile(index)}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Text style={styles.removeIcon}>✕</Text>
                </TouchableOpacity>
              )}
              {showProgress && (
                <View style={styles.uploadOverlay}>
                  <View style={styles.progressBarTrack}>
                    <View style={[styles.progressBarFill, { width: `${uploadProgress}%` }]} />
                  </View>
                  <Text style={styles.progressText}>{uploadProgress}%</Text>
                </View>
              )}
              {isSubmitting && !showProgress && (
                <View style={styles.uploadOverlay}>
                  <ActivityIndicator size="small" color="#FFFFFF" />
                </View>
              )}
            </View>
          ))}
        </View>
      )}

      {canAddMore && (
        <TouchableOpacity style={styles.addButton} onPress={handlePickFiles} activeOpacity={0.7}>
          <Text style={styles.addIcon}>+</Text>
          <Text style={styles.addText}>Add photos or files</Text>
        </TouchableOpacity>
      )}

      {error ? <Text style={styles.errorText}>{error}</Text> : null}
    </View>
  );
});

AttachmentPicker.displayName = 'AttachmentPicker';

const makeStyles = ({ semantic }: ThemeContextValue) => StyleSheet.create({
  container: {
    gap: 8,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: semantic.text.primary,
  },
  counter: {
    fontSize: 12,
    color: semantic.text.muted,
  },
  previewGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  previewItem: {
    width: 88,
    height: 88,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: semantic.background.tertiary,
    position: 'relative',
  },
  previewImage: {
    width: '100%',
    height: '100%',
  },
  filePreview: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 6,
    gap: 2,
  },
  fileName: {
    fontSize: 10,
    color: semantic.text.secondary,
    textAlign: 'center',
  },
  fileSize: {
    fontSize: 9,
    color: semantic.text.muted,
  },
  removeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeIcon: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '700',
  },
  uploadOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
  },
  progressBarTrack: {
    width: '70%',
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 2,
  },
  progressText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: semantic.border.secondary,
    borderStyle: 'dashed',
  },
  addIcon: {
    fontSize: 18,
    color: semantic.interactive.primary,
    fontWeight: '600',
  },
  addText: {
    fontSize: 14,
    color: semantic.interactive.primary,
    fontWeight: '500',
  },
  errorText: {
    fontSize: 12,
    color: semantic.status.error,
    marginTop: 2,
  },
});
